import { useState, useMemo, useEffect } from 'react'
import {
  FilterState,
  LeadRecord,
  OpportunityRecord,
  LoanOfficer,
} from '../types/throughput'
import { generateMockData } from '../utils/mockThroughputData'
import {
  filterData,
  calculateFunnelMetrics,
  calculateOfficerMetrics,
} from '../utils/throughputCalculations'
import { subDays, format } from 'date-fns'

export function useThroughputData() {
  const [data, setData] = useState<{
    leads: LeadRecord[]
    opportunities: OpportunityRecord[]
    officers: LoanOfficer[]
  } | null>(null)

  const [filters, setFilters] = useState<FilterState>({
    dateRange: {
      start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    },
    scopeType: 'All',
    includeInactive: false,
    leadSources: [],
    groupBySource: false,
  })

  useEffect(() => {
    const mock = generateMockData()
    setData(mock)
  }, [])

  const filteredData = useMemo(() => {
    if (!data) return null
    return filterData(data.leads, data.opportunities, data.officers, filters)
  }, [data, filters])

  // Company-wide data (same date/source filters but scope = All)
  const companyData = useMemo(() => {
    if (!data) return null
    const companyFilters: FilterState = {
      ...filters,
      scopeType: 'All',
      selectedScopeId: undefined,
    }
    return filterData(
      data.leads,
      data.opportunities,
      data.officers,
      companyFilters,
    )
  }, [data, filters])

  const metrics = useMemo(() => {
    if (!filteredData || !data) return null

    const funnelMetrics = calculateFunnelMetrics(
      filteredData.filteredLeads,
      filteredData.filteredOpportunities,
    )

    const scopeOfficers = data.officers.filter((o) =>
      filteredData.scopedOfficerIds.includes(o.id),
    )
    const officerMetrics = calculateOfficerMetrics(
      scopeOfficers,
      filteredData.filteredLeads,
      filteredData.filteredOpportunities,
    )

    return {
      funnelMetrics,
      officerMetrics,
      totalLeads: filteredData.filteredLeads.length,
      totalConverted: filteredData.filteredOpportunities.length,
      totalFunded: filteredData.filteredOpportunities.filter(
        (o) => o.fundedDate,
      ).length,
    }
  }, [filteredData, data])

  // Company averages per officer
  const companyAverages = useMemo(() => {
    if (!companyData || !data) return null

    const activeOfficers = data.officers.filter(
      (o) => o.role === 'Loan Officer' && o.isActive,
    )
    if (activeOfficers.length === 0) return null

    const allOfficerMetrics = calculateOfficerMetrics(
      activeOfficers,
      companyData.filteredLeads,
      companyData.filteredOpportunities,
    )

    const count = allOfficerMetrics.length
    const sum = allOfficerMetrics.reduce(
      (acc, m) => ({
        claimed: acc.claimed + m.counts['Created'],
        converted: acc.converted + m.counts['Converted'],
        submittedToUW: acc.submittedToUW + m.counts['Submitted to UW'],
        ctc: acc.ctc + m.counts['Closing Scheduled'],
        funded: acc.funded + m.counts['Funded'],
        conversionRate: acc.conversionRate + m.conversion.createdToConverted,
        pullThrough: acc.pullThrough + m.conversion.createdToFunded,
      }),
      {
        claimed: 0,
        converted: 0,
        submittedToUW: 0,
        ctc: 0,
        funded: 0,
        conversionRate: 0,
        pullThrough: 0,
      },
    )

    return {
      claimed: Math.round(sum.claimed / count),
      converted: Math.round(sum.converted / count),
      submittedToUW: Math.round(sum.submittedToUW / count),
      ctc: Math.round(sum.ctc / count),
      funded: Math.round(sum.funded / count),
      conversionRate: sum.conversionRate / count,
      pullThrough: sum.pullThrough / count,
      officerCount: count,
    }
  }, [companyData, data])

  return {
    isLoading: !data,
    filters,
    setFilters,
    metrics,
    companyAverages,
    officers: data?.officers || [],
    filteredLeads: filteredData?.filteredLeads || [],
    filteredOpportunities: filteredData?.filteredOpportunities || [],
  }
}
